<template>
  <main v-if="data" class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div class="d-flex align-items-center">
          <h1 class="h2">
            {{ data.name }}
          </h1>
        </div>
        <div
            v-if="data.excerpt_en"
            class="small mb-2"
            v-html="data.excerpt_en"
        ></div>
        <div class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              About {{ data.name }}
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">IATA</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtImg
                        height="18"
                        v-if="data.logo_small_url"
                        class="me-1 mt-n1"
                        loading="lazy"
                        :src="data.logo_small_url"
                        :alt="data.name + ' icon'"
                    />
                    {{ data.iata }}
                  </p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">ICAO</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.icao }}</p>
                </div>
              </div>
              <div class="col-6" v-if="data.founded">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Founded</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ moment(data.founded).format('MMMM YYYY') }}</p>
                </div>
              </div>
              <div class="col-6" v-if="data.country">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Based</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.country.name }}
                  </p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Alliance</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.alliance ?? '-' }}</p>
                </div>
              </div>
              <div class="col-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal truncate">Frequent Flyer Program</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.frequent_flyer_program ?? '-' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.fleet" class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Aircraft (fleet)
            </h2>
            <div v-if="data.frequent_flyer_program !== 'Flying Blue'" class="alert alert-warning">
              <p class="small mb-0">We only show aircraft that have been used on Flying Blue related flights in the past.</p>
            </div>
            <div class="row g-2 mb-3">
              <div v-if="data.fleet.boeing" class="col">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal truncate">Boeing</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.fleet.boeing }}</p>
                </div>
              </div>
              <div v-if="data.fleet.airbus" class="col">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal truncate">Airbus</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.fleet.airbus }}</p>
                </div>
              </div>
              <div v-if="data.fleet.embraer" class="col">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal truncate">Embraer</h3>
                  <p class="mb-0 fw-bold mb-n1">{{ data.fleet.embraer }}</p>
                </div>
              </div>
            </div>
            <aircraft-table
                v-if="data.fleet.aircrafts"
                :aircrafts="data.fleet.aircrafts"
                :airline="data"
            />
          </div>
        </div>

        <div v-if="data.lounges.length" class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              {{ data.iata === 'KL' ? 'KLM Crown Lounges' : data.name + ' Lounges' }}
            </h2>
            <lounge-table :lounges="data.lounges" />
          </div>
        </div>

        <div class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Social Media / Support
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-6 col-md-3" v-if="data.instagram_handle">
                <div class="card card-social p-3 bg-blurry">
                  <div class="d-flex align-items-center">
                    <div class="icon">
                      <NuxtImg
                          width="19"
                          height="19"
                          loading="lazy"
                          src="img/ig-logo.svg"
                          class="opacity-75"
                          alt="Instagram Logo"
                      />
                    </div>
                    <div>
                      <p class="mb-0 small text-uppercase opacity-75" style="margin-top: -2px">Instagram</p>
                      <p class="mb-0 fw-bold mb-n1">
                        <a :href="'https://instagram.com/' + data.instagram_handle"
                           :title="data.name + ' on Instagram'"
                           target="_blank"
                           class="inherit stretched-link"
                           rel="nofollow noopener"
                        >
                          {{ data.instagram_handle }}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-md-3" v-if="data.facebook_handle">
                <div class="card card-social p-3 bg-blurry">
                  <div class="d-flex align-items-center">
                    <div class="icon">
                      <NuxtImg
                          width="19"
                          height="19"
                          loading="lazy"
                          src="img/fb-logo.svg"
                          class="opacity-75"
                          alt="Facebook Logo"
                      />
                    </div>
                    <div>
                      <p class="mb-0 small text-uppercase opacity-75" style="margin-top: -2px">Facebook</p>
                      <p class="mb-0 fw-bold mb-n1">
                        <a :href="'https://facebook.com/' + data.facebook_handle"
                           :title="data.name + ' on Instagram'"
                           target="_blank"
                           class="inherit stretched-link"
                           rel="nofollow noopener"
                        >
                          {{ data.facebook_handle }}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-md-3" v-if="data.x_handle">
                <div class="card card-social p-3 bg-blurry">
                  <div class="d-flex align-items-center">
                    <div class="icon">
                      <NuxtImg
                          width="19"
                          height="19"
                          loading="lazy"
                          src="img/x-logo.svg"
                          class="opacity-75"
                          alt="X Logo"
                      />
                    </div>
                    <div>
                      <p class="mb-0 small text-uppercase opacity-75" style="margin-top: -2px">X / Twitter</p>
                      <p class="mb-0 fw-bold mb-n1">
                        <a :href="'https://x.com/' + data.x_handle"
                           :title="data.name + ' on X / Twitter'"
                           target="_blank"
                           class="inherit stretched-link"
                           rel="nofollow noopener"
                        >
                          {{ data.facebook_handle }}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6 col-md-3" v-if="data.tiktok_handle">
                <div class="card card-social p-3 bg-blurry">
                  <div class="d-flex align-items-center">
                    <div class="icon">
                      <NuxtImg
                          width="19"
                          height="19"
                          loading="lazy"
                          src="img/tt-logo.svg"
                          class="opacity-75"
                          alt="Tiktok Logo"
                      />
                    </div>
                    <div>
                      <p class="mb-0 small text-uppercase opacity-75" style="margin-top: -2px">Tiktok</p>
                      <p class="mb-0 fw-bold mb-n1">
                        <a :href="'https://tiktok.com/@' + data.tiktok_handle"
                           :title="data.name + ' on Tiktok'"
                           target="_blank"
                           class="inherit stretched-link"
                           rel="nofollow noopener"
                        >
                          {{ data.tiktok_handle }}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
          <div class="col-12">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Frequently Asked Questions</h2>
          </div>
          <Question>
            <template v-slot:question>
              Is {{ data.short_name ?? data.name }} a member of the Flying Blue loyalty program?
            </template>
            <template v-slot:answer>
              <p v-if="data.frequent_flyer_program === 'Flying Blue'" class="mb-0">Good news, {{ data.name }} is a member of the Flying Blue loyalty program. This means you can earn Flying Blue Miles with {{ data.short_name ?? data.name }}, as {{ data.short_name ?? data.name }} is a partner of the Flying Blue program.</p>
              <p v-else class="mb-0">Unfortunately, {{ data.name }} is not a member of the Flying Blue loyalty program. This means you cannot earn Flying Blue Miles with {{ data.short_name ?? data.name }}.</p>
            </template>
          </Question>
          <Question v-if="data.fleet">
            <template v-slot:question>
              How many aircraft does {{ data.name }} have in its fleet?
            </template>
            <template v-slot:answer>
              <p>{{ data.name }} has {{ data.fleet.boeing + data.fleet.airbus + data.fleet.embraer }} aircraft in its fleet.</p>
              <ul>
                <li v-if="data.fleet.boeing">Boeing: {{ data.fleet.boeing }}</li>
                <li v-if="data.fleet.airbus">Airbus: {{ data.fleet.airbus }}</li>
                <li v-if="data.fleet.embraer">Embraer: {{ data.fleet.embraer }}</li>
              </ul>
              <p class="mb-0">Note: These are only aircraft that have been used on Flying Blue related flights! If an aircraft from {{ data.name }} has not yet had a Flying Blue flight, we do not show it.</p>
              <p v-if="data.parent" class="mb-0 mt-3">{{ data.name }} is a subsidiary of {{ data.parent.name }}. On the page of <NuxtLink :to="{ name: 'airlines-slug', params: { slug: data.parent.slug }}" :title="'Aircraft of: ' + data.parent.name">{{ data.parent.name }}</NuxtLink> you can see a complete overview of all aircraft.</p>
            </template>
          </Question>
          <Question v-if="data.iata === 'KL'">
            <template v-slot:question>
              When are the KLM World Deal Weeks?
            </template>
            <template v-slot:answer>
              <p>You can book cheap KLM tickets during the annual KLM World Deal Weeks / Real Deal Days. During this period, you can book cheap tickets to many destinations.</p>
              <p class="mb-0">For exact dates and more information, read our blog article about <a title="When are the KLM World Deal Weeks" href="https://rewards.flights/blog/when-are-the-klm-world-deal-weeks-2024">When are the KLM World Deal Weeks</a>.</p>
            </template>
          </Question>
          <Question v-if="data.lounges.length">
            <template v-slot:question>
              What are {{ data.iata === 'KL' ? 'KLM Crown' : data.name }} Lounges?
            </template>
            <template v-slot:answer>
              <p>{{ data.iata === 'KL' ? 'KLM Crown' : data.name }} Lounges are exclusive lounges at airports worldwide, designed to provide travelers with a comfortable and luxurious environment.</p>
              <p class="mb-0">The {{ data.iata === 'KL' ? 'KLM Crown' : data.name }} Lounges offer various amenities such as free Wi-Fi, food and drinks, workspaces, and relaxation areas. They are ideal for travelers who want to relax or work before their flight.</p>
            </template>
          </Question>
          <Question v-if="data.founded">
            <template v-slot:question>
              When was {{ data.name }} founded?
            </template>
            <template v-slot:answer>
              <p class="mb-0">{{ data.name }} was founded in {{ moment(data.founded).format('MMMM YYYY') }} and is located in {{ data.country.name }}.</p>
            </template>
          </Question>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import moment from "moment/moment";
import LoungeTable from "~/components/LoungeTable.vue";
const route = useRoute()
const runtimeConfig = useRuntimeConfig()


const {data: fetchedData} = await useAsyncData(
    'airline-' + route.params.slug,
    async () => {
      return $fetch(runtimeConfig.public.backendUrl + "/api/v2/airlines/" + route.params.slug, {
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        }
      })
    }
);

if (!fetchedData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'No airline found'
  })
}

const data = ref(fetchedData.value.airline);

useSeoMeta({
  title: data.value.name,
  ogTitle: data.value.name,
  twitterTitle: data.value.name,
  description: 'Discover ' + data.value.name + ' (' + data.value.iata + '), founded in ' + moment(data.value.founded).format('YYYY') + (data.value.alliance ? ', and a member of the ' + data.value.alliance + ' alliance' : ', and part of ' + data.value.frequent_flyer_program) + '. Find the latest offers, fleet information and flight schedules.',
  ogDescription: 'Discover ' + data.value.name + ' (' + data.value.iata + '), founded in ' + moment(data.value.founded).format('YYYY') + (data.value.alliance ? ', and a member of the ' + data.value.alliance + ' alliance' : ', and part of ' + data.value.frequent_flyer_program) + '. Find the latest offers, fleet information and flight schedules.',
  twitterDescription: 'Discover ' + data.value.name + ' (' + data.value.iata + '), founded in ' + moment(data.value.founded).format('YYYY') + (data.value.alliance ? ', and a member of the ' + data.value.alliance + ' alliance' : ', and part of ' + data.value.frequent_flyer_program) + '. Find the latest offers, fleet information and flight schedules.',
  ogUrl: runtimeConfig.public.baseUrl + "/airlines/" + data.value.slug,
  ogImage: data.value.logo_url,
  twitterImage: data.value.logo_url,
  twitterImageAlt: 'Logo ' + data.value.name,
  ogLocale: "en",
  ogLocaleAlternate: "nl",
});

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/airlines/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/airlines/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/airlines/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/luchtvaartmaatschappijen/" + data.value.slug
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Airlines',
            item: runtimeConfig.public.baseUrl + '/airlines',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.name,
          },
        ],
      },
      {
        '@type': 'Organization',
        name: data.value.name,
        logo: data.value.logo_url,
        url: data.value.website,
        description: data.value.name + ' is a leading international airline',
        location: data.value.country.name,
        foundingDate: moment(data.value.founded).toISOString(),
        sameAs: [
          data.value.website,
          'https://instagram.com/' + data.value.instagram_handle,
          'https://facebook.com/' + data.value.facebook_handle,
          'https://x.com/' + data.value.x_handle,
          'https://tiktok.com/@' + data.value.tiktok_handle
        ],
        memberOf: {
          '@type': 'Organization',
          name: data.value.alliance,
        },
      }
    ]
  }
});

</script>